import { Analytics } from '@cobliteam/analytics';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { Location } from '@cobliteam/common-types';
import { useCreateRiskRecognitionSilence } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions/useCreateRiskRecognitionSilence';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useRightPanelStore from '@cobliteam/hooks/src/useRightPanelStore/useRightPanelStore';
import { Environment } from '@cobliteam/react-environments';
import { KnockFeedProvider } from '@knocklabs/react';
import { type TransitionEvent, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useRiskRecognition, { buildRiskRecognitionEventProps, isHighlightRenderable } from '../../store/useRiskRecognition';
import { getFeedButtonLabelsFromItem } from './feedHighlight';
import RiskRecognitionCard from './RiskRecognitionCard';
import useRiskRecognitionFeedHighlight from './useRiskRecognitionFeedHighlight';

const trackDisplayedOncePerBucket = (highlight: RiskRecognitionHighlight) => {
  const { dismissedBucketEnd, lastDisplayedBucketEnd, markDisplayed } = useRiskRecognition.getState();
  if (!isHighlightRenderable(highlight, dismissedBucketEnd) || highlight.time_bucket_end === lastDisplayedBucketEnd) return;
  Analytics.track('AI - Risk Recognition - Displayed', buildRiskRecognitionEventProps(highlight, 'card'));
  markDisplayed(highlight.time_bucket_end);
};

const RiskRecognitionFloatingCardInner = () => {
  const { knockCardRiskRecognition } = useFeatureFlags();
  const { pathname, search } = useLocation();
  const history = useHistory();
  const { feedClient, feedItems, riskRecognitionFeedItem } = useRiskRecognitionFeedHighlight();

  const dismissedBucketEnd = useRiskRecognition((s) => s.dismissedBucketEnd);
  const routeFollowBucketEnd = useRiskRecognition((s) => s.routeFollowBucketEnd);
  const dismiss = useRiskRecognition((s) => s.dismiss);
  const openDrawer = useRiskRecognition((s) => s.openDrawer);
  const { mutate: createSilence } = useCreateRiskRecognitionSilence();

  const isRightPanelHidden = useRightPanelStore((s) => s.isRightPanelHidden);

  const highlight = riskRecognitionFeedItem?.highlight;
  const isFollowingOnTracking = pathname === Location.TRACKING && !!routeFollowBucketEnd && highlight?.time_bucket_end === routeFollowBucketEnd;
  const renderAllowed = knockCardRiskRecognition && (pathname === Location.MONITORING || isFollowingOnTracking);
  const canRenderHighlight = renderAllowed && isHighlightRenderable(highlight, dismissedBucketEnd) && !!highlight?.metadata;

  // Keyed by bucket (not a boolean) so the next bucket's fresh highlight never
  // inherits a stale "exiting" flag and auto-dismisses.
  const [exitingBucketEnd, setExitingBucketEnd] = useState<string | null>(null);
  const [exitingItemId, setExitingItemId] = useState<string | null>(null);

  // Fire "Displayed" once per bucket as a renderable Knock event arrives, and mark the feed item
  // as seen because custom Knock surfaces own their engagement calls.
  useEffect(() => {
    if (!(canRenderHighlight && highlight && riskRecognitionFeedItem)) return;

    trackDisplayedOncePerBucket(highlight);
    if (!riskRecognitionFeedItem.item.seen_at) void feedClient.markAsSeen(riskRecognitionFeedItem.item);
  }, [canRenderHighlight, feedClient, highlight, riskRecognitionFeedItem]);

  if (!(canRenderHighlight && riskRecognitionFeedItem)) return null;

  const isExiting = exitingBucketEnd === highlight.time_bucket_end;
  const { action: actionLabel, dismiss: dismissLabel } = getFeedButtonLabelsFromItem(riskRecognitionFeedItem.item);

  const handleDismiss = () => {
    Analytics.track('AI - Risk Recognition - Dismissed', buildRiskRecognitionEventProps(highlight, 'card'));
    // Fire the personal silence (~2h) in parallel with the exit animation. Guarded:
    // without a detection_id (backend not yet shipping it) we still hide locally.
    const detectionId = highlight.metadata?.detection_id;
    if (detectionId) createSilence({ detectionId, reason: 'dismissed' });
    setExitingItemId(riskRecognitionFeedItem.item.id);
    setExitingBucketEnd(highlight.time_bucket_end);
  };

  const handleExitEnd = (event: TransitionEvent<HTMLDivElement>) => {
    // The opacity leg settles the dismissal; the transform leg fires its own transitionend we ignore.
    // Dismiss the captured exiting bucket, not the live highlight — a new bucket may have arrived mid-animation.
    if (event.propertyName !== 'opacity' || !exitingBucketEnd) return;
    const exitingItem = feedItems.find((item) => item.id === exitingItemId);
    if (exitingItem) void feedClient.markAsArchived(exitingItem);
    dismiss(exitingBucketEnd);
    setExitingBucketEnd(null);
    setExitingItemId(null);
  };

  const handleAction = () => {
    Analytics.track('AI - Risk Recognition - Action Clicked', buildRiskRecognitionEventProps(highlight, 'card'));
    void feedClient.markAsInteracted(riskRecognitionFeedItem.item);
    // Abre o drawer pelo store (conteúdo imediato) E reflete na URL (`?risk-recognition-drawer=<id>`) pra
    // virar deep link compartilhável. O resolver do drawer não re-busca: ele exige `!highlight`, e o store já tem.
    openDrawer(highlight);
    const params = new URLSearchParams(search);
    params.set('risk-recognition-drawer', riskRecognitionFeedItem.item.id);
    history.push({ search: `?${params.toString()}` });
  };

  return (
    <RiskRecognitionCard
      highlight={highlight}
      actionLabel={actionLabel}
      dismissLabel={dismissLabel}
      isExiting={isExiting}
      isRightPanelHidden={isRightPanelHidden}
      onDismiss={handleDismiss}
      onAction={handleAction}
      onTransitionEnd={handleExitEnd}
    />
  );
};

const RiskRecognitionFloatingCard = () => {
  const { knockCardRiskRecognition, knockInApp } = useFeatureFlags();
  const { getSession } = useUser();
  const session = getSession();
  const userId = session?.user?.id;
  const fleetId = session?.fleet?.id;
  const { knockAPIKey, knockFeedChannelId } = Environment.getInstance().getEnvironment();

  const defaultFeedOptions = useMemo(
    () => ({
      archived: 'exclude' as const,
      mode: 'rich' as const,
      // Sem filtro `source` no servidor: a API do Knock só aceita um valor, e há vários workflows de
      // risco. Buscamos mais itens e filtramos client-side por RISK_RECOGNITION_WORKFLOW_KEYS + shape.
      page_size: 10,
      status: 'all' as const,
      ...(fleetId ? { tenant: String(fleetId) } : {}),
    }),
    [fleetId]
  );

  if (!(knockCardRiskRecognition && knockInApp && knockAPIKey && knockFeedChannelId && userId)) return null;

  return (
    <KnockFeedProvider feedId={knockFeedChannelId} defaultFeedOptions={defaultFeedOptions}>
      <RiskRecognitionFloatingCardInner />
    </KnockFeedProvider>
  );
};

export default RiskRecognitionFloatingCard;
